import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarClock, Mail, Send, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import WeekOffPicker from '../common/WeekOffPicker';
import { payrollAPI } from '../../services/api';
import { getErrorMessage } from '../../lib/utils';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// 1-28 only: February cannot reach 29-31, and a job that never fires in
// February looks exactly like one that is switched off.
const DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

/** The month payroll covers: always the one before today. */
const previousMonth = (now = new Date()) => {
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return { month: d.getMonth() + 1, year: d.getFullYear() };
};

const PayrollScheduleCard = () => {
  const [schedule, setSchedule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [period, setPeriod] = useState(previousMonth());
  const [readiness, setReadiness] = useState(null);
  const [sending, setSending] = useState(false);

  const loadSchedule = useCallback(async () => {
    try {
      const { data } = await payrollAPI.getPayrollSchedule();
      setSchedule(data);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not load the payroll calendar'));
    }
  }, []);

  const loadReadiness = useCallback(async () => {
    try {
      const { data } = await payrollAPI.payslipReadiness(period.month, period.year);
      setReadiness(data);
    } catch (e) {
      setReadiness(null);
    }
  }, [period.month, period.year]);

  useEffect(() => { loadSchedule(); }, [loadSchedule]);
  useEffect(() => { loadReadiness(); }, [loadReadiness]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await payrollAPI.savePayrollSchedule({ ...schedule, ...patch });
      setSchedule(data);
      toast.success('Payroll calendar saved');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not save the calendar'));
      await loadSchedule();
    } finally {
      setSaving(false);
    }
  };

  const send = async () => {
    setSending(true);
    try {
      const { data } = await payrollAPI.sendPayslips(period.month, period.year);
      toast.success(`${data.sent} payslip(s) sent`, {
        description: [
          data.skipped_already_sent ? `${data.skipped_already_sent} already sent` : null,
          data.skipped_no_email ? `${data.skipped_no_email} without an email address` : null,
          data.failed ? `${data.failed} failed` : null,
        ].filter(Boolean).join(' · ') || undefined,
      });
      await loadReadiness();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Payslips were not sent'));
    } finally {
      setSending(false);
    }
  };

  if (!schedule) return null;

  const daySelect = (value, onChange, id) => (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
    >
      {DAYS.map((d) => <option key={d} value={d}>{ordinal(d)}</option>)}
    </select>
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarClock className="h-4 w-4" />Payroll calendar
          </CardTitle>
          <p className="text-[13px] text-muted-foreground">
            Payroll always covers the <b>previous</b> month — the current month&apos;s
            attendance is not complete until it ends. The run is processed automatically
            and left for a human to approve; nothing is paid or emailed without that.
          </p>
          {schedule.inherited_from_tenant_id && (
            <Badge variant="soft" className="w-fit">
              inherited from parent tenant #{schedule.inherited_from_tenant_id}
            </Badge>
          )}
        </CardHeader>
        <CardContent className="grid gap-5 md:grid-cols-2">
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="run-day" className="text-sm">Process payroll automatically</Label>
              <Switch
                checked={Boolean(schedule.payroll_auto_run_enabled)}
                disabled={saving}
                onCheckedChange={(v) => save({ payroll_auto_run_enabled: v })}
              />
            </div>
            {daySelect(schedule.payroll_run_day, (v) => save({ payroll_run_day: v }), 'run-day')}
            <p className="text-[11px] text-muted-foreground">
              Leave a few days after month end so attendance regularisations settle first.
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="email-day" className="text-sm">Email payslips automatically</Label>
              <Switch
                checked={Boolean(schedule.payslip_email_enabled)}
                disabled={saving}
                onCheckedChange={(v) => save({ payslip_email_enabled: v })}
              />
            </div>
            {daySelect(schedule.payslip_email_day, (v) => save({ payslip_email_day: v }), 'email-day')}
            <p className="text-[11px] text-muted-foreground">
              Payslips are only sent once the run is approved. If it is not approved by
              this day, nothing goes out and the run appears in the Action Center.
            </p>
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="skip-non-working" className="text-sm">
                Wait for a working day
              </Label>
              <Switch
                id="skip-non-working"
                checked={Boolean(schedule.payslip_email_skip_non_working)}
                disabled={saving}
                onCheckedChange={(v) => save({ payslip_email_skip_non_working: v })}
              />
            </div>
            <p className="text-[11px] text-muted-foreground">
              When the email day lands on a week-off or holiday, payslips go out on the
              next working day instead — so nobody gets a payslip with nobody in to
              answer questions about it.
            </p>
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label className="text-sm">Week off</Label>
            <WeekOffPicker
              value={schedule.week_off_days}
              disabled={saving}
              onChange={(days) => save({ week_off_days: days })}
            />
            <p className="text-[11px] text-muted-foreground">
              Payroll divides pay by the days actually worked, so this decides what a
              day of loss-of-pay costs. A shift can keep its own week — set that on the
              shift, and it overrides this for whoever is on it.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="h-4 w-4" />Send payslips now
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-xs text-muted-foreground">Month</Label>
              <select
                value={period.month}
                onChange={(e) => setPeriod({ ...period, month: Number(e.target.value) })}
                className="mt-1 h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Year</Label>
              <input
                type="number"
                value={period.year}
                onChange={(e) => setPeriod({ ...period, year: Number(e.target.value) })}
                className="mt-1 h-10 w-28 rounded-md border border-input bg-background px-3 text-sm"
              />
            </div>
            <Button onClick={send} disabled={sending || !readiness?.can_send}>
              <Send className="mr-2 h-4 w-4" />
              {sending ? 'Sending…' : 'Send payslips'}
            </Button>
          </div>

          {readiness && !readiness.can_send && (
            <div className="flex gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[13px] text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <b>{readiness.reason}</b>
                <div className="mt-1">{readiness.next_step}</div>
              </div>
            </div>
          )}

          {readiness?.can_send && (
            <div className="flex gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-[13px] text-emerald-800">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                Payroll is approved and ready to send.
                {readiness.already_sent > 0 && (
                  <> {readiness.already_sent} payslip(s) already went out
                    {readiness.sent_at ? ` on ${new Date(readiness.sent_at).toLocaleDateString()}` : ''} —
                    sending again only mails the ones still outstanding.</>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PayrollScheduleCard;
